import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  FormControl,
  OutlinedInput,
  InputLabel,
  MenuItem,
  Select,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider
} from '@mui/material';
import {
  MoneyOff as MoneyOffIcon,
  CheckCircle as CheckCircleIcon,
  AccountBalance as AccountBalanceIcon,
  Send as SendIcon
} from '@mui/icons-material';
import { useMutation } from 'react-query';
import apiClient from '../../request/http-common';

const JointAccountWithdraw = ({ open, onClose, jointAccount, onSuccess }) => {
  const [amount, setAmount] = useState('');
  const [selectedCoin, setSelectedCoin] = useState('');
  const [withdrawalAddress, setWithdrawalAddress] = useState('');
  const [description, setDescription] = useState('');
  const [currentStep, setCurrentStep] = useState('form'); // form, confirm, success

  // Available withdrawal methods
  const withdrawalOptions = [
    { 
      symbol: 'BTC', 
      name: 'Bitcoin',
      image: 'https://cryptologos.cc/logos/bitcoin-btc-logo.png',
      network: 'Bitcoin'
    },
    { 
      symbol: 'ETH', 
      name: 'Ethereum',
      image: 'https://cryptologos.cc/logos/ethereum-eth-logo.png',
      network: 'Ethereum'
    },
    { 
      symbol: 'USDC', 
      name: 'USDC',
      image: 'https://res.cloudinary.com/dyz8kppso/image/upload/v1752850333/dc07jds0ueozxgsbrzrf.png',
      network: 'ERC20'
    },
    { 
      symbol: 'USDT', 
      name: 'USDT',
      image: 'https://cryptologos.cc/logos/tether-usdt-logo.png',
      network: 'TRC20'
    }
  ];

  const { mutate: createWithdrawal, isLoading: isCreating } = useMutation(
    async (withdrawalData) => {
      const response = await apiClient.post(`/api/joint-accounts/${jointAccount.id}/transactions`, {
        transaction_type: 'withdrawal',
        amount: parseFloat(withdrawalData.amount),
        description: withdrawalData.description || `${withdrawalData.coin} withdrawal from joint account`,
        metadata: {
          coin: withdrawalData.coin,
          network: withdrawalData.network,
          withdrawal_address: withdrawalData.address,
          requires_approval: true
        }
      });
      return response.data;
    },
    {
      onSuccess: () => {
        setCurrentStep('success');
        onSuccess?.();
      },
      onError: (error) => {
        console.error('Withdrawal creation failed:', error);
      }
    }
  );

  const formatPrice = (amount) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount || 0);

  const handleSubmit = () => {
    if (!amount || !selectedCoin || !withdrawalAddress) {
      return;
    }

    const selectedOption = withdrawalOptions.find(opt => opt.symbol === selectedCoin);
    
    createWithdrawal({
      amount: amount,
      coin: selectedCoin,
      network: selectedOption?.network,
      address: withdrawalAddress,
      description: description
    });
  };

  const handleClose = () => {
    setCurrentStep('form');
    setAmount('');
    setSelectedCoin('');
    setWithdrawalAddress('');
    setDescription('');
    onClose();
  };

  const isFormValid = amount && selectedCoin && withdrawalAddress && 
    parseFloat(amount) > 0 && parseFloat(amount) <= (jointAccount?.balance || 0);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
          border: '1px solid #2B3139',
          borderRadius: '16px'
        }
      }}
    >
      <DialogTitle sx={{ 
        color: '#FFFFFF', 
        borderBottom: '1px solid #2B3139',
        display: 'flex',
        alignItems: 'center',
        gap: 2
      }}>
        <MoneyOffIcon sx={{ color: '#F84960' }} />
        Withdraw from Joint Account
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {currentStep === 'form' && (
          <Box>
            {/* Account Balance Info */}
            <Card sx={{
              background: 'linear-gradient(135deg, #1E2328 0%, #2B3139 100%)',
              border: '1px solid #3B4148',
              mb: 3
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <AccountBalanceIcon sx={{ color: '#F7931A' }} />
                  <Typography variant="h6" sx={{ color: '#FFFFFF' }}>
                    Available Balance
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ color: '#10B981', fontWeight: 700 }}>
                  {formatPrice(jointAccount?.balance)}
                </Typography>
              </CardContent>
            </Card>

            {/* Withdrawal Form */}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel sx={{ color: '#6C7293' }}>Amount to Withdraw</InputLabel>
              <OutlinedInput
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                type="number"
                placeholder="0.00"
                sx={{
                  color: '#FFFFFF',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#2B3139'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#F7931A'
                  }
                }}
              />
              {amount && parseFloat(amount) > (jointAccount?.balance || 0) && (
                <Typography variant="caption" sx={{ color: '#F84960', mt: 1 }}>
                  Amount exceeds available balance
                </Typography>
              )}
            </FormControl>

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel sx={{ color: '#6C7293' }}>Withdrawal Method</InputLabel>
              <Select
                value={selectedCoin}
                onChange={(e) => setSelectedCoin(e.target.value)}
                sx={{
                  color: '#FFFFFF',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#2B3139'
                  }
                }}
              >
                {withdrawalOptions.map((option) => (
                  <MenuItem key={option.symbol} value={option.symbol}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar src={option.image} sx={{ width: 24, height: 24 }}>
                        {option.symbol[0]}
                      </Avatar>
                      {option.name} ({option.symbol}) - {option.network}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel sx={{ color: '#6C7293' }}>Withdrawal Address</InputLabel>
              <OutlinedInput
                value={withdrawalAddress}
                onChange={(e) => setWithdrawalAddress(e.target.value)}
                placeholder="Enter wallet address"
                sx={{
                  color: '#FFFFFF',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#2B3139'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#F7931A'
                  }
                }}
              />
            </FormControl>

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel sx={{ color: '#6C7293' }}>Description (Optional)</InputLabel>
              <OutlinedInput
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Purpose of withdrawal"
                multiline
                rows={3}
                sx={{
                  color: '#FFFFFF',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#2B3139'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#F7931A'
                  }
                }}
              />
            </FormControl>

            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Important:</strong> This withdrawal requires admin approval and approval from joint account members 
                based on the account's approval settings. Ensure the withdrawal address is correct as transactions cannot be reversed.
              </Typography>
            </Alert>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={handleClose}
                sx={{
                  borderColor: '#6C7293',
                  color: '#6C7293',
                  flex: 1
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={!isFormValid || isCreating}
                sx={{
                  background: 'linear-gradient(135deg, #F84960 0%, #DC2626 100%)',
                  flex: 1,
                  '&:disabled': {
                    background: '#2B3139'
                  }
                }}
              >
                {isCreating ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1, color: '#FFFFFF' }} />
                    Processing...
                  </>
                ) : (
                  'Submit Withdrawal Request'
                )}
              </Button>
            </Box>
          </Box>
        )}

        {currentStep === 'success' && (
          <Box sx={{ textAlign: 'center' }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: '#10B981', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#FFFFFF', mb: 2 }}>
              Withdrawal Request Submitted
            </Typography>
            <Typography variant="body2" sx={{ color: '#6C7293', mb: 2 }}>
              Your withdrawal request for <strong>{formatPrice(amount)}</strong> has been submitted.
            </Typography>
            <Typography variant="body2" sx={{ color: '#6C7293', mb: 3 }}>
              The request is now pending approval from joint account members and admin verification. 
              You will be notified of any status changes.
            </Typography>
            
            <Box sx={{
              background: 'linear-gradient(135deg, #1E2328 0%, #2B3139 100%)',
              border: '1px solid #3B4148',
              borderRadius: '8px',
              p: 2,
              mb: 3,
              textAlign: 'left'
            }}>
              <Typography variant="subtitle2" sx={{ color: '#FFFFFF', mb: 1 }}>
                Withdrawal Details:
              </Typography>
              <Typography variant="body2" sx={{ color: '#6C7293' }}>
                Amount: {formatPrice(amount)}
              </Typography>
              <Typography variant="body2" sx={{ color: '#6C7293' }}>
                Method: {withdrawalOptions.find(opt => opt.symbol === selectedCoin)?.name}
              </Typography>
              <Typography variant="body2" sx={{ color: '#6C7293', wordBreak: 'break-all' }}>
                Address: {withdrawalAddress}
              </Typography>
              {description && (
                <Typography variant="body2" sx={{ color: '#6C7293' }}>
                  Description: {description}
                </Typography>
              )}
            </Box>

            <Button
              variant="contained"
              onClick={handleClose}
              sx={{
                background: 'linear-gradient(135deg, #F7931A 0%, #FFB800 100%)'
              }}
            >
              Close
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default JointAccountWithdraw; 